import { AlertTriangle, ArrowDown, ArrowUp, Plus, RotateCcw, Save, Search, Send, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAdminAuth } from '../auth/AdminAuthProvider'
import { hasMinRole } from '../auth/adminAuthorization'
import { discardCmsDraft, fetchCmsContent, fetchCmsVersions, formatDateTime, publishCmsContent, rollbackCmsContent, saveCmsDraft } from '../lib/cms'
import { validateFaqDraft } from '../../../../packages/validation/cms'
import { EMPTY_FAQ_ITEM, FAQ_SCHEMA_VERSION } from '../../../../packages/types/cms'

const ACTIVE_LABELS = { all: '전체', active: '활성', archived: 'archive' }
// 카테고리는 자유 입력이지만(문서는 category is string만 요구), 챗봇 쪽 src/data/faqData.js의
// CATEGORY_SUGGESTIONS와 같은 값을 쓰면 기존 인텐트 매칭(chatSearch.js의 INTENT_ALLOW_CATEGORIES)과
// 어긋나지 않는다 — datalist 힌트로만 제공하고 admin 앱이 src/ 내부를 직접 import하지는 않는다.
const FAQ_CATEGORY_HINTS = ['기본', '학사', '수강신청', '장학', '도서관', '셔틀버스', '캠퍼스맵', '학식', '연락처']

function diffSummary(productionItems, currentItems) {
  const prodMap = new Map((productionItems || []).map(item => [item.id, item]))
  const curMap = new Map(currentItems.map(item => [item.id, item]))
  const added = currentItems.filter(item => !prodMap.has(item.id)).length
  const removed = (productionItems || []).filter(item => !curMap.has(item.id)).length
  const changed = currentItems.filter(item => prodMap.has(item.id) && JSON.stringify(prodMap.get(item.id)) !== JSON.stringify(item)).length
  return { added, removed, changed }
}

function newId() {
  return typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `faq-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

export default function FaqPage() {
  const auth = useAdminAuth()
  const canEdit = hasMinRole(auth.role, 'editor')
  const [params, setParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [production, setProduction] = useState(null)
  const [draft, setDraft] = useState(null)
  const [items, setItems] = useState([])
  const [versions, setVersions] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const savedSnapshotRef = useRef('[]')

  const search = params.get('q') || ''
  const categoryFilter = params.get('category') || 'all'
  const activeFilter = params.get('active') || 'all'

  async function load() {
    setLoading(true); setError('')
    try {
      const { production: prod, draft: dr } = await fetchCmsContent('faq')
      setProduction(prod); setDraft(dr)
      const initial = dr?.data?.items ?? prod?.data?.items ?? []
      setItems(initial)
      savedSnapshotRef.current = JSON.stringify(initial)
      const list = await fetchCmsVersions('faq')
      setVersions(list)
    } catch (err) { setError(err.message) } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  const dirty = JSON.stringify(items) !== savedSnapshotRef.current
  const validation = useMemo(() => validateFaqDraft({ schemaVersion: FAQ_SCHEMA_VERSION, items }), [items])
  const diff = useMemo(() => diffSummary(production?.data?.items, items), [production, items])

  const categories = useMemo(() => [...new Set([...FAQ_CATEGORY_HINTS, ...items.map(item => item.category).filter(Boolean)])].sort(), [items])
  const filtered = useMemo(() => items
    .filter(item => (categoryFilter === 'all' || item.category === categoryFilter)
      && (activeFilter === 'all' || (activeFilter === 'active' ? item.active !== false : item.active === false))
      && (!search || `${item.question} ${item.answer} ${(item.keywords || []).join(' ')}`.toLowerCase().includes(search.toLowerCase())))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [items, search, categoryFilter, activeFilter])

  const selected = items.find(item => item.id === selectedId) || null
  const selectedErrors = selectedId ? validation.errors.filter(entry => entry.id === selectedId || entry.index === items.findIndex(i => i.id === selectedId)) : []

  function setFilter(key, value) { const next = new URLSearchParams(params); value && value !== 'all' ? next.set(key, value) : next.delete(key); setParams(next, { replace: true }) }
  function updateSelected(patch) { setItems(current => current.map(item => item.id === selectedId ? { ...item, ...patch } : item)) }

  function addItem() {
    const nextOrder = items.length ? Math.max(...items.map(item => item.order ?? 0)) + 1 : 0
    const item = { ...EMPTY_FAQ_ITEM, id: newId(), order: nextOrder }
    setItems(current => [...current, item])
    setSelectedId(item.id)
  }

  function toggleArchive(item) {
    setItems(current => current.map(entry => entry.id === item.id ? { ...entry, active: !(entry.active !== false) } : entry))
  }

  function moveItem(item, direction) {
    const idx = filtered.findIndex(entry => entry.id === item.id)
    const swapIdx = idx + direction
    if (idx < 0 || swapIdx < 0 || swapIdx >= filtered.length) return
    const other = filtered[swapIdx]
    setItems(current => current.map(entry => {
      if (entry.id === item.id) return { ...entry, order: other.order }
      if (entry.id === other.id) return { ...entry, order: item.order }
      return entry
    }))
  }

  async function saveDraft() {
    setBusy(true); setMessage(''); setError('')
    try {
      await saveCmsDraft('faq', { schemaVersion: FAQ_SCHEMA_VERSION, items }, { uid: auth.user.uid, baseVersion: production?.version ?? 0 })
      savedSnapshotRef.current = JSON.stringify(items)
      const { draft: fresh } = await fetchCmsContent('faq')
      setDraft(fresh)
      setMessage('초안을 저장했습니다.')
    } catch (err) { setError(err.message) } finally { setBusy(false) }
  }

  async function discardDraft() {
    if (!window.confirm('저장된 초안을 버리고 배포된 내용으로 되돌릴까요?')) return
    setBusy(true); setMessage(''); setError('')
    try { await discardCmsDraft('faq', auth.user.uid); await load(); setMessage('초안을 버렸습니다.') }
    catch (err) { setError(err.message) } finally { setBusy(false) }
  }

  async function publish() {
    if (!validation.valid) { setError('치명적 오류가 있어 배포할 수 없습니다. 오류 목록을 확인하세요.'); return }
    setBusy(true); setMessage(''); setError('')
    try {
      const version = await publishCmsContent('faq', { schemaVersion: FAQ_SCHEMA_VERSION, items }, { uid: auth.user.uid })
      await load()
      setMessage(`v${version}으로 배포했습니다.`)
    } catch (err) { setError(err.validation ? '치명적 오류가 있어 배포할 수 없습니다.' : err.message) } finally { setBusy(false) }
  }

  async function rollback(version) {
    if (!window.confirm(`v${version}으로 롤백할까요? 현재 미저장 초안은 사라집니다.`)) return
    setBusy(true); setMessage(''); setError('')
    try { const next = await rollbackCmsContent('faq', version, { uid: auth.user.uid }); await load(); setMessage(`v${version} → v${next}로 롤백했습니다.`) }
    catch (err) { setError(err.message) } finally { setBusy(false) }
  }

  if (loading) return <div className="empty-state">FAQ를 불러오는 중…</div>
  return (
    <>
      <header className="page-header"><div><p className="eyebrow">콘텐츠</p><h1>자주 묻는 질문</h1><p>앱에서 사용하는 질문과 답변을 관리합니다. 배포하면 즉시 반영됩니다.</p></div>
        {canEdit && <div className="header-actions"><button className="button secondary" type="button" onClick={addItem}><Plus size={16} />새 FAQ</button></div>}
      </header>

      {error && <div className="error-state">{error}</div>}
      {message && <div className="temporary-warning">{message}</div>}

      <section className="panel" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', fontSize: 12 }}>
            <span className={`status-badge ${dirty ? 'new' : 'published'}`}>{dirty ? '미저장 변경 있음' : '변경 없음'}</span>
            <span className="muted">production v{production?.version ?? 0}{draft ? ` · 초안 저장 ${formatDateTime(draft.savedAt)}` : ' · 초안 없음'}</span>
            <span className="muted">production 대비 추가 {diff.added} · 수정 {diff.changed} · 삭제 {diff.removed}</span>
            {validation.errors.length > 0 && <span className="status-badge ended"><AlertTriangle size={11} style={{ verticalAlign: -1 }} /> 오류 {validation.errors.length}</span>}
            {validation.warnings.length > 0 && <span className="status-badge new">경고 {validation.warnings.length}</span>}
          </div>
          {canEdit && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {draft && <button className="button secondary" type="button" disabled={busy} onClick={discardDraft}><RotateCcw size={16} />초안 버리기</button>}
              <button className="button secondary" type="button" disabled={busy || !dirty} onClick={saveDraft}><Save size={16} />초안 저장</button>
              <button className="button primary" type="button" disabled={busy || !validation.valid} onClick={publish}><Send size={16} />검증 후 배포</button>
              {versions.length > 0 && (
                <select className="select" style={{ width: 200 }} aria-label="버전 롤백" disabled={busy} value="" onChange={e => e.target.value && rollback(Number(e.target.value))}>
                  <option value="">이전 버전으로 롤백…</option>
                  {versions.map(v => <option key={v.version} value={v.version}>v{v.version} · {formatDateTime(v.publishedAt)}{v.note ? ` (${v.note})` : ''}</option>)}
                </select>
              )}
            </div>
          )}
        </div>
        {validation.errors.length > 0 && (
          <ul style={{ margin: '12px 0 0', paddingLeft: 18, fontSize: 12, color: 'var(--danger)' }}>
            {validation.errors.slice(0, 8).map((entry, idx) => <li key={idx}>{entry.id ? `[${entry.id}] ` : ''}{entry.field}: {entry.message}</li>)}
            {validation.errors.length > 8 && <li>외 {validation.errors.length - 8}건…</li>}
          </ul>
        )}
      </section>

      <section className={`panel split-view ${selected ? 'has-selection' : ''}`}>
        <div className="split-list">
          <div className="toolbar">
            <label className="search-field"><Search size={16} /><input className="input" aria-label="FAQ 검색" placeholder="질문·답변·키워드 검색" value={search} onChange={e => setFilter('q', e.target.value)} /></label>
            <select className="select" aria-label="카테고리 필터" value={categoryFilter} onChange={e => setFilter('category', e.target.value)}><option value="all">전체 카테고리</option>{categories.map(c => <option key={c} value={c}>{c}</option>)}</select>
            <select className="select" aria-label="활성 필터" value={activeFilter} onChange={e => setFilter('active', e.target.value)}>{Object.entries(ACTIVE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          </div>
          <div className="split-list-items">
            {filtered.length === 0 ? <div className="empty-state">조건에 맞는 FAQ가 없습니다.</div> : filtered.map((item, idx) => (
              <button type="button" key={item.id} className={`split-item ${item.id === selectedId ? 'active' : ''}`} onClick={() => setSelectedId(item.id)}>
                <div className="split-item-top">
                  <strong>{item.question || '(질문 없음)'}</strong>
                  <span className={`status-badge ${item.active === false ? 'ended' : 'published'}`}>{item.active === false ? 'archive' : '활성'}</span>
                </div>
                <p>{item.category} · 순서 {item.order ?? 0}</p>
                {canEdit && (
                  <div style={{ display: 'flex', gap: 4, marginTop: 6 }} onClick={e => e.stopPropagation()}>
                    <button type="button" className="icon-button" style={{ width: 30, height: 30 }} disabled={idx === 0} onClick={() => moveItem(item, -1)} aria-label="위로"><ArrowUp size={14} /></button>
                    <button type="button" className="icon-button" style={{ width: 30, height: 30 }} disabled={idx === filtered.length - 1} onClick={() => moveItem(item, 1)} aria-label="아래로"><ArrowDown size={14} /></button>
                    <button type="button" className="icon-button" style={{ width: 30, height: 30 }} onClick={() => toggleArchive(item)} aria-label="archive 토글"><Trash2 size={14} /></button>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        <article className="detail-panel">
          {!selected ? <div className="empty-state">왼쪽 목록에서 FAQ를 선택하거나 새로 만드세요.</div> : (
            <div className="editor-layout">
              <div className="editor-form">
                <section className="panel">
                  <div className="form-section"><h2>질문 · 답변</h2><div className="field-grid">
                    <div className="field full"><label>질문</label><textarea className="textarea" style={{ minHeight: 60 }} disabled={!canEdit} value={selected.question} onChange={e => updateSelected({ question: e.target.value })} /></div>
                    <div className="field full"><label>답변</label><textarea className="textarea" disabled={!canEdit} value={selected.answer} onChange={e => updateSelected({ answer: e.target.value })} /></div>
                    <div className="field"><label>카테고리</label><input className="input" list="faq-categories" disabled={!canEdit} value={selected.category} onChange={e => updateSelected({ category: e.target.value })} /><datalist id="faq-categories">{categories.map(c => <option key={c} value={c} />)}</datalist></div>
                    <div className="field"><label>순서</label><input className="input" type="number" disabled={!canEdit} value={selected.order ?? 0} onChange={e => updateSelected({ order: Number(e.target.value) })} /></div>
                    <div className="field full"><label>검색 키워드 (쉼표로 구분)</label><input className="input" disabled={!canEdit} value={(selected.keywords || []).join(', ')} onChange={e => updateSelected({ keywords: e.target.value.split(',').map(k => k.trim()).filter(Boolean) })} /></div>
                    <div className="field"><span className="label">활성 여부</span><label className="check-chip"><input type="checkbox" disabled={!canEdit} checked={selected.active !== false} onChange={e => updateSelected({ active: e.target.checked })} />활성 (해제 시 archive)</label></div>
                    <div className="field"><label>신뢰도</label><select className="select" disabled={!canEdit} value={selected.confidence || 'medium'} onChange={e => updateSelected({ confidence: e.target.value })}><option value="high">high</option><option value="medium">medium</option></select></div>
                  </div></div>
                  <div className="form-section"><h2>추가 정보 (선택)</h2><div className="field-grid">
                    <div className="field full"><label>공식 URL</label><input className="input" disabled={!canEdit} placeholder="https://…" value={selected.officialUrl || ''} onChange={e => updateSelected({ officialUrl: e.target.value })} /></div>
                    <div className="field"><label>출처</label><input className="input" disabled={!canEdit} value={selected.source || ''} onChange={e => updateSelected({ source: e.target.value })} /></div>
                    <div className="field"><label>학년도</label><input className="input" disabled={!canEdit} value={selected.year || ''} onChange={e => updateSelected({ year: e.target.value })} /></div>
                    <div className="field"><label>학기</label><input className="input" disabled={!canEdit} value={selected.semester || ''} onChange={e => updateSelected({ semester: e.target.value })} /></div>
                  </div></div>
                </section>
                {selectedErrors.length > 0 && <div className="error-state" style={{ marginTop: 12 }}>{selectedErrors.map((e, i) => <div key={i}>{e.field}: {e.message}</div>)}</div>}
              </div>
              <aside className="preview-column"><div className="phone-preview"><div className="phone-screen"><div className="phone-status"><span>9:41</span><span>SU-Guide</span></div><div className="phone-app">
                <p>수기 즉답</p>
                <div className="announcement-preview banner">
                  <strong>{selected.question || '질문 미리보기'}</strong>
                  <p style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>{selected.answer || '답변 미리보기'}</p>
                </div>
              </div></div></div></aside>
            </div>
          )}
        </article>
      </section>
    </>
  )
}
